import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { NotPermitted } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { cyclesApi } from '../../api/cycles-api';
import type {
  IAdminParticipantCycleAccess,
  IAdminParticipantDetail
} from '../../api/participant-contracts';
import { refusalMessage } from '../../refusal';
import { NOT_REREAD, NoteLine, type ISaidNote } from './said';

/**
 * Who can start (SR-487 ruling 17), in a team's Team section: each pin that admits only its
 * access list — `listed`, not archived, of a survey about the team — and whether the list admits
 * this team, by whom and when, as the card sends it (`cycleAccess`). Add and Remove write the
 * pin's list for this one participant under `rankings.manage`, drawn off with the reason
 * otherwise, and read the record again. Drawn only where the card names such a pin: an API before
 * SR-487 sends none, and with no `listed` pin anyone eligible starts.
 */
export function CycleAccessPart({
  member,
  headingLevel,
  reread
}: {
  member: IAdminParticipantDetail;
  headingLevel: 3 | 4;
  reread: () => Promise<boolean>;
}) {
  const permitted = useCan()('rankings.manage');
  const reasonId = React.useId();
  const [busy, setBusy] = React.useState<Nullable<string>>(null);
  const [said, setSaid] = React.useState<Nullable<ISaidNote>>(null);
  const entries = member.cycleAccess ?? [];
  if (entries.length === 0) return null;
  const Heading = headingLevel === 3 ? 'h3' : 'h4';

  const change = async (entry: IAdminParticipantCycleAccess) => {
    const key = pinKey(entry);
    setBusy(key);
    setSaid(null);
    try {
      const ids = [member.participantId];
      if (entry.admitted) await cyclesApi.removeAccess(entry.cycleCode, entry.surveyCode, ids);
      else await cyclesApi.addAccess(entry.cycleCode, entry.surveyCode, ids);
    } catch (failure) {
      setSaid({
        tone: 'refused',
        text: refusalMessage(failure, 'The access list was not changed')
      });
      setBusy(null);
      return;
    }
    if (!(await reread())) setSaid({ tone: 'done', text: `Saved.${NOT_REREAD}` });
    setBusy(null);
  };

  return (
    <section aria-label="Survey access" className="grid gap-3">
      <Heading className="font-semibold text-[var(--color-royal-blue)]">Survey access</Heading>
      <ul className="grid gap-2">
        {entries.map((entry) => (
          <li
            key={pinKey(entry)}
            data-cycle-access={pinKey(entry)}
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
          >
            <span className="font-medium">
              {entry.surveyCode} · {entry.cycleCode}
            </span>
            {entry.admitted ? (
              <span>
                On the access list
                {entry.addedBy && ` — added by ${entry.addedBy.email}`}
                {entry.addedAt && `, ${formatDateTime(entry.addedAt)}`}
              </span>
            ) : (
              <span className="text-muted-foreground">Not on the access list</span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!permitted || busy !== null}
              aria-describedby={permitted ? undefined : reasonId}
              onClick={() => void change(entry)}
            >
              {busy === pinKey(entry) ? 'Saving…' : entry.admitted ? 'Remove' : 'Add'}
            </Button>
          </li>
        ))}
      </ul>
      {!permitted && <NotPermitted id={reasonId} what="changing who can start a survey" />}
      {said && <NoteLine note={said} />}
    </section>
  );
}

/** A pin's key: its survey and its cycle. */
function pinKey(entry: IAdminParticipantCycleAccess): string {
  return `${entry.surveyCode}/${entry.cycleCode}`;
}
